// RecordOnBehalfForm — spec decision 4 / §C5: "Staff may fill a form in on
// behalf of a recipient. Staff may NEVER sign."
//
// The gap this closes: today the ONLY writer is the recipient's tokenized form,
// which stamps `applicant:<id>`. So when Michelle Garcia emailed her answers in,
// there was no honest way to record them — the choice was to type them in as if
// she had submitted them, or not record them at all. This path stamps the truth
// (`staff:<sub>` acting `onBehalfOf` the recipient) and the viewer says so.
//
// WHY THIS IS PER-KIND RATHER THAN GENERATED FROM THE REPLICA. The obvious
// design — generate inputs from the ViewDocReplica the read-only view already
// renders — cannot work: the replica describes fields by their recipient-facing
// LABEL, and W9's write path is typed by DOMAIN FIELD, because every write runs
// through the recipient's own validators (normalizeDesignation, normalizeAnimal,
// the landlord's zod schema). Joining the two through display copy would break
// silently the next time someone improves a question's wording. So each form
// below is typed against the SAME submission interface the recipient's own page
// posts (IntakeDesignationData, AnimalInput, VerificationSubmissionBody) — one
// shared type, checked by the compiler, is a stronger guarantee than a
// hand-copied duplicate. The animals editor goes one step further (WEL-173): it
// renders the household forms' own question component (AnimalQuestionFields)
// over their own draft shape (lib/animalDraft.ts) and holds staff to the same
// required answers, so a staff-entered animal is asked, and stored, exactly as
// a household-entered one.
//
// This component renders only where mayRecordOnBehalf() allows it and
// recordUnavailableReason() is null. It does not re-derive those rules; the
// caller owns the gate, and the gate refuses every signable outright.

import { useEffect, useState, type ReactNode } from 'react';
import { toast } from '@/components/Toast';
import { ApiError } from '@/lib/api';
import { ConsequenceConfirm } from '@/components/oc/ConsequenceConfirm';
import type { AnimalInput, IntakeDesignationData } from '@/lib/intakeClient';
import { AnimalQuestionFields } from '@/components/AnimalQuestionFields';
import {
  animalDraftGaps,
  blankAnimalAnswers,
  draftToWireAnswers,
  staffAnimalGaps,
  type AnimalAnswerDraft,
} from '@/lib/animalDraft';
import type {
  VerificationScreening,
  VerificationSubmissionBody,
} from '@/lib/verificationClient';
import type {
  OwnerIntakeAnswers,
  OwnerIntakeQuestion,
  OwnerIntakeSection,
} from '@/lib/ownerIntakeClient';
import { buildRecordBody, ownerMissing, ownerQuestionUnanswered } from '@/lib/viewDocs';
import {
  fetchOwnerIntakeQuestions,
  recordDocOnBehalf,
  type DocRecipient,
  type RecordChannel,
  type RecordOnBehalfBody,
  type RecordAnswers,
} from '@/lib/viewDocsClient';

// ---------------------------------------------------------------------------
// Shared field primitives — thin wrappers on the design system's .field
// ---------------------------------------------------------------------------

let seq = 0;
const nextId = () => `vd-f${++seq}`;

/**
 * Merge a patch, DELETING any key whose value is undefined rather than
 * assigning undefined to it.
 *
 * Not a TypeScript workaround — a semantic one. "They did not answer" is the
 * ABSENCE of a field, not a field holding undefined, and the recipient's own
 * submission types say so by declaring their optionals without `| undefined`.
 * Sending `{ spayedNeutered: undefined }` would put an empty answer on the
 * record where there should be no answer at all.
 */
function merge<T extends object>(base: T, patch: Record<string, unknown>): T {
  const next: Record<string, unknown> = { ...(base as Record<string, unknown>) };
  for (const [key, value] of Object.entries(patch)) {
    if (value === undefined) delete next[key];
    else next[key] = value;
  }
  return next as T;
}

function TextField({
  label,
  value,
  onChange,
  required,
  invalid,
  helper,
  type = 'text',
  disabled,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  required?: boolean;
  invalid?: boolean;
  helper?: string;
  type?: 'text' | 'email' | 'tel' | 'date' | 'number';
  disabled?: boolean;
}): React.JSX.Element {
  const [id] = useState(nextId);
  return (
    <div className="field">
      <label htmlFor={id}>
        {label}
        {required ? '' : ' (optional)'}
      </label>
      <input
        id={id}
        type={type}
        className="input"
        value={value}
        disabled={disabled}
        {...(required ? { required: true } : {})}
        {...(invalid ? { 'aria-invalid': 'true' as const } : {})}
        onChange={(e) => onChange(e.target.value)}
      />
      {helper && <span className="helper">{helper}</span>}
    </div>
  );
}

/** Tri-state yes/no: unanswered stays unanswered rather than defaulting to No. */
function YesNoField({
  label,
  value,
  onChange,
  disabled,
}: {
  label: string;
  value: boolean | undefined;
  onChange: (v: boolean | undefined) => void;
  disabled?: boolean;
}): React.JSX.Element {
  const [id] = useState(nextId);
  return (
    <div className="field">
      <label htmlFor={id}>{label}</label>
      <select
        id={id}
        className="input"
        disabled={disabled}
        value={value === undefined ? '' : value ? 'yes' : 'no'}
        onChange={(e) =>
          onChange(e.target.value === '' ? undefined : e.target.value === 'yes')
        }
      >
        <option value="">They did not say</option>
        <option value="yes">Yes</option>
        <option value="no">No</option>
      </select>
    </div>
  );
}

function ChoiceField<T extends string>({
  label,
  value,
  options,
  onChange,
  required,
  invalid,
  disabled,
}: {
  label: string;
  value: T | undefined;
  options: Array<{ value: T; label: string }>;
  onChange: (v: T | undefined) => void;
  required?: boolean;
  invalid?: boolean;
  disabled?: boolean;
}): React.JSX.Element {
  const [id] = useState(nextId);
  return (
    <div className="field">
      <label htmlFor={id}>{label}</label>
      <select
        id={id}
        className="input"
        disabled={disabled}
        value={value ?? ''}
        {...(required ? { required: true } : {})}
        {...(invalid ? { 'aria-invalid': 'true' as const } : {})}
        onChange={(e) => onChange((e.target.value || undefined) as T | undefined)}
      >
        <option value="">Choose…</option>
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </div>
  );
}

function TextAreaField({
  label,
  value,
  onChange,
  required,
  invalid,
  disabled,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  required?: boolean;
  invalid?: boolean;
  disabled?: boolean;
}): React.JSX.Element {
  const [id] = useState(nextId);
  return (
    <div className="field">
      <label htmlFor={id}>
        {label}
        {required ? '' : ' (optional)'}
      </label>
      <textarea
        id={id}
        className="input"
        rows={3}
        value={value}
        disabled={disabled}
        {...(required ? { required: true } : {})}
        {...(invalid ? { 'aria-invalid': 'true' as const } : {})}
        onChange={(e) => onChange(e.target.value)}
      />
    </div>
  );
}

function FieldGrid({ children }: { children: ReactNode }): React.JSX.Element {
  return <div className="vd-record-grid">{children}</div>;
}

// ---------------------------------------------------------------------------
// Per-kind answer editors
//
// Each owns a draft of the recipient's OWN submission type and reports whether
// it is complete enough to send. "Complete enough" is deliberately the minimum
// that makes the record meaningful — the recipient's real validators run
// server-side and their message is surfaced verbatim, so this client does not
// try to second-guess them.
// ---------------------------------------------------------------------------

interface EditorProps<T> {
  draft: T;
  setDraft: (next: T) => void;
  attempted: boolean;
  disabled: boolean;
}

function DesignationEditor({
  draft,
  setDraft,
  attempted,
  disabled,
}: EditorProps<IntakeDesignationData>): React.JSX.Element {
  const set = (patch: Record<string, unknown>) => setDraft(merge(draft, patch));
  return (
    <>
      <TextField
        label="Designee's name"
        value={draft.designeeName ?? ''}
        onChange={(v) => set({ designeeName: v })}
        required
        invalid={attempted && !draft.designeeName?.trim()}
        disabled={disabled}
      />
      <FieldGrid>
        <TextField
          label="Relationship to the household"
          value={draft.relationship ?? ''}
          onChange={(v) => set({ relationship: v })}
          disabled={disabled}
        />
        <TextField
          label="Phone"
          type="tel"
          value={draft.phone ?? ''}
          onChange={(v) => set({ phone: v })}
          disabled={disabled}
        />
        <TextField
          label="Email"
          type="email"
          value={draft.email ?? ''}
          onChange={(v) => set({ email: v })}
          disabled={disabled}
        />
      </FieldGrid>
      <TextField
        label="Mailing address"
        value={draft.addressLine1 ?? ''}
        onChange={(v) => set({ addressLine1: v })}
        disabled={disabled}
      />
      <FieldGrid>
        <TextField
          label="Apartment or unit"
          value={draft.addressLine2 ?? ''}
          onChange={(v) => set({ addressLine2: v })}
          disabled={disabled}
        />
        <TextField
          label="City"
          value={draft.city ?? ''}
          onChange={(v) => set({ city: v })}
          disabled={disabled}
        />
        <TextField
          label="State"
          value={draft.state ?? ''}
          onChange={(v) => set({ state: v })}
          disabled={disabled}
        />
        <TextField
          label="ZIP"
          value={draft.zip ?? ''}
          onChange={(v) => set({ zip: v })}
          disabled={disabled}
        />
      </FieldGrid>
    </>
  );
}

function YardCareEditor({
  draft,
  setDraft,
  attempted,
  disabled,
}: EditorProps<{ arrangement?: 'resident_maintains' | 'lessor_arranges' }>): React.JSX.Element {
  return (
    <ChoiceField
      label="Who takes care of the yard?"
      value={draft.arrangement}
      required
      invalid={attempted && !draft.arrangement}
      disabled={disabled}
      options={[
        { value: 'resident_maintains' as const, label: 'The residents maintain it' },
        { value: 'lessor_arranges' as const, label: 'WHP arranges yard care' },
      ]}
      onChange={(arrangement) => setDraft(merge(draft, { arrangement }))}
    />
  );
}

/**
 * One animal as staff enter it: the SAME per-animal answers the household
 * forms edit (lib/animalDraft.ts, WEL-173), so what staff record on someone's
 * behalf has exactly the shape — and the questions — a household-entered animal
 * has. The id only keys the row; it is never shown.
 */
interface StaffAnimalDraft extends AnimalAnswerDraft {
  id: string;
}

function newAnimal(): StaffAnimalDraft {
  const id =
    typeof crypto !== 'undefined' && 'randomUUID' in crypto
      ? crypto.randomUUID()
      : `a-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
  return { id, ...blankAnimalAnswers() };
}

/** A staff row → the recipient's own submission shape (only what its kind is asked). */
function staffAnimalToInput(a: StaffAnimalDraft): AnimalInput {
  return { id: a.id, ...draftToWireAnswers(a) };
}

function AnimalsEditor({
  draft,
  setDraft,
  attempted,
  disabled,
}: EditorProps<StaffAnimalDraft[]>): React.JSX.Element {
  const patch = (i: number, next: Partial<AnimalAnswerDraft>) =>
    setDraft(draft.map((a, j) => (i === j ? { ...a, ...next } : a)));

  return (
    <>
      {draft.length === 0 && (
        <div className="oc-meta">
          No animals recorded yet. Add one, or leave the list empty if they told
          us they have none.
        </div>
      )}
      {draft.map((animal, i) => (
        <div key={animal.id} className="vd-record-animal">
          <div className="vd-record-animal-head">
            <span className="vd-replica-title">
              {animal.name.trim() || `Animal ${i + 1}`}
            </span>
            <button
              type="button"
              className="btn"
              data-variant="ghost"
              data-size="sm"
              disabled={disabled}
              onClick={() => setDraft(draft.filter((_, j) => j !== i))}
            >
              Remove
            </button>
          </div>
          {/* WEL-173: the household's own questions — what kind of animal, then
              only that kind's (a dog's breed, weight and age; a cat's age and
              color; an aquarium's tank size). */}
          <AnimalQuestionFields
            idPrefix={`vd-animal-${animal.id}`}
            value={animal}
            disabled={disabled}
            onChange={(next) => patch(i, next)}
            licenseHelper="Leave it blank if they don't have one yet."
            {...(attempted ? { invalid: animalDraftGaps(animal) } : {})}
          />
        </div>
      ))}
      <div>
        <button
          type="button"
          className="btn"
          data-variant="secondary"
          data-size="sm"
          disabled={disabled}
          onClick={() => setDraft([...draft, newAnimal()])}
        >
          Add an animal
        </button>
      </div>
    </>
  );
}

/**
 * The landlord's screening questions, as data rather than 17 hand-written
 * blocks — same questions, same order, and the follow-up appears only once the
 * answer calls for it, exactly as it does on their page.
 */
const SCREENING: Array<{
  key: keyof VerificationScreening;
  ask: string;
  detailKey?: keyof VerificationScreening;
  detailLabel?: string;
  /** Which answer opens the follow-up. */
  detailWhen?: boolean;
}> = [
  {
    key: 'properNotice',
    ask: 'Did they give proper notice?',
    detailKey: 'properNoticeTerms',
    detailLabel: 'What were the notice terms?',
    detailWhen: false,
  },
  {
    key: 'eviction',
    ask: 'Was an eviction ever filed?',
    detailKey: 'evictionExplain',
    detailLabel: 'What happened?',
    detailWhen: true,
  },
  {
    key: 'nsfOrLate',
    ask: 'Any late or returned payments?',
    detailKey: 'nsfLateDetail',
    detailLabel: 'How often, and how late?',
    detailWhen: true,
  },
  {
    key: 'outstandingDebt',
    ask: 'Is anything still owed?',
    detailKey: 'debtDetail',
    detailLabel: 'How much, and for what?',
    detailWhen: true,
  },
  {
    key: 'leaseViolations',
    ask: 'Were there lease violations?',
    detailKey: 'violationsExplain',
    detailLabel: 'What were they?',
    detailWhen: true,
  },
  {
    key: 'pets',
    ask: 'Did they have animals?',
    detailKey: 'petsDetail',
    detailLabel: 'Which animals?',
    detailWhen: true,
  },
  {
    key: 'otherConcerns',
    ask: 'Any other concerns?',
    detailKey: 'otherConcernsDetail',
    detailLabel: 'What concerns?',
    detailWhen: true,
  },
  {
    key: 'rentAgain',
    ask: 'Would they rent to them again?',
    detailKey: 'rentAgainWhyNot',
    detailLabel: 'Why not?',
    detailWhen: false,
  },
];

function VerificationEditor({
  draft,
  setDraft,
  attempted,
  disabled,
}: EditorProps<VerificationSubmissionBody>): React.JSX.Element {
  const set = (patch: Record<string, unknown>) => setDraft(merge(draft, patch));
  const screening: VerificationScreening = draft.screening ?? {};
  const setScreening = (patch: Record<string, unknown>) =>
    set({ screening: merge(screening, patch) });
  const confirmed = draft.confirmed ?? {};
  const setConfirmed = (patch: Record<string, unknown>) =>
    set({ confirmed: merge(confirmed, patch) });

  return (
    <>
      <div className="oc-kicker">Who told us</div>
      <FieldGrid>
        <TextField
          label="Their name"
          value={draft.respondent?.name ?? ''}
          onChange={(v) => set({ respondent: merge(draft.respondent ?? {}, { name: v }) })}
          required
          invalid={attempted && !draft.respondent?.name?.trim()}
          disabled={disabled}
        />
        <TextField
          label="Their email"
          type="email"
          value={draft.respondent?.email ?? ''}
          onChange={(v) => set({ respondent: merge(draft.respondent ?? {}, { email: v }) })}
          disabled={disabled}
        />
        <TextField
          label="Their phone"
          type="tel"
          value={draft.respondent?.phone ?? ''}
          onChange={(v) => set({ respondent: merge(draft.respondent ?? {}, { phone: v }) })}
          disabled={disabled}
        />
      </FieldGrid>

      <div className="oc-kicker">The tenancy</div>
      <YesNoField
        label="Did they say these people never rented from them?"
        value={draft.neverRented}
        disabled={disabled}
        onChange={(neverRented) => set({ neverRented })}
      />
      {!draft.neverRented && (
        <>
          <YesNoField
            label="Are they related to the applicants?"
            value={draft.related}
            disabled={disabled}
            onChange={(related) => set({ related })}
          />
          {draft.related && (
            <TextField
              label="How are they related?"
              value={draft.relationshipExplain ?? ''}
              onChange={(v) => set({ relationshipExplain: v })}
              disabled={disabled}
            />
          )}
          <YesNoField
            label="Are they still living there?"
            value={confirmed.stillResiding}
            disabled={disabled}
            onChange={(stillResiding) => setConfirmed({ stillResiding })}
          />
          <FieldGrid>
            <TextField
              label="Moved in"
              type="date"
              value={confirmed.moveIn ?? ''}
              onChange={(v) => setConfirmed({ moveIn: v })}
              disabled={disabled}
            />
            {!confirmed.stillResiding && (
              <TextField
                label="Moved out"
                type="date"
                value={confirmed.moveOut ?? ''}
                onChange={(v) => setConfirmed({ moveOut: v })}
                disabled={disabled}
              />
            )}
            <TextField
              label="Monthly rent"
              type="number"
              value={confirmed.monthlyRent === undefined ? '' : String(confirmed.monthlyRent)}
              onChange={(v) => setConfirmed({ monthlyRent: v === '' ? undefined : Number(v) })}
              disabled={disabled}
            />
            <TextField
              label="How many others lived there"
              type="number"
              value={screening.othersCount === undefined ? '' : String(screening.othersCount)}
              onChange={(v) => setScreening({ othersCount: v === '' ? undefined : Number(v) })}
              disabled={disabled}
            />
          </FieldGrid>

          <div className="oc-kicker">What they said</div>
          {SCREENING.map((q) => {
            const answer = screening[q.key] as boolean | undefined;
            const showDetail = q.detailKey && answer !== undefined && answer === q.detailWhen;
            return (
              <div key={String(q.key)}>
                <YesNoField
                  label={q.ask}
                  value={answer}
                  disabled={disabled}
                  onChange={(v) => setScreening({ [q.key]: v })}
                />
                {showDetail && q.detailKey && (
                  <TextField
                    label={q.detailLabel ?? 'Details'}
                    value={(screening[q.detailKey] as string | undefined) ?? ''}
                    onChange={(v) => setScreening({ [q.detailKey as string]: v })}
                    disabled={disabled}
                  />
                )}
              </div>
            );
          })}
        </>
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// Owner intake — rendered from the server's own question list
//
// The only form here that is not hand-written, and deliberately so. Its
// conditional logic (winterization instructions only when winterizing is
// needed; the which-HOA select only when they are in an HOA, populated from
// the live HOA list) belongs to the owner's real form, and copying it into the
// viewer would drift the moment that form changed. So the server computes the
// applicable questions with the SAME function the owner's own page calls, and
// this renders whatever comes back. The join is `question.id`, which IS the
// OwnerIntakeAnswers key — a stable field, never display copy.
// ---------------------------------------------------------------------------

const OWNER_SECTION_LABEL: Record<OwnerIntakeSection, string> = {
  taxes_insurance: 'Taxes, insurance and mortgage',
  irrigation: 'Irrigation and winterizing',
  hoa: 'HOA',
  notice: 'Where notices go',
};

const OWNER_SECTION_ORDER: OwnerIntakeSection[] = [
  'taxes_insurance',
  'irrigation',
  'hoa',
  'notice',
];

/** The answered value for a question, as the field wants it. */
function ownerValue(answers: OwnerIntakeAnswers, q: OwnerIntakeQuestion): unknown {
  return (answers as Record<string, unknown>)[q.id as string];
}

function OwnerIntakeEditor({
  id,
  draft,
  setDraft,
  attempted,
  disabled,
  reloadToken,
}: {
  id: string;
  draft: { answers: OwnerIntakeAnswers; questions: OwnerIntakeQuestion[]; fillable: boolean; loaded: boolean };
  setDraft: (next: {
    answers: OwnerIntakeAnswers;
    questions: OwnerIntakeQuestion[];
    fillable: boolean;
    loaded: boolean;
  }) => void;
  attempted: boolean;
  disabled: boolean;
  /** Bumped after each successful record — answering a gate reveals dependents. */
  reloadToken: number;
}): React.JSX.Element {
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let live = true;
    setLoading(true);
    setError(null);
    void (async () => {
      try {
        const view = await fetchOwnerIntakeQuestions(id);
        if (!live) return;
        // Seed from what is ON FILE — and DELIBERATELY NOT from a
        // confirm-with-owner question's staffValue.
        //
        // The owner's own form pre-selects it, and rightly: the owner is
        // sitting there, and their submit IS the confirmation. A staff recorder
        // is in a different position — they are writing down what the owner
        // said on the phone. A pre-selected answer nobody discussed, recorded
        // on that owner's behalf, would be a fabricated confirmation, which is
        // the exact dishonesty this whole path exists to remove. So the
        // proposal is shown IN THE PROMPT and the field starts empty; staff
        // must record what the owner actually said. Left untouched it sends no
        // key at all, and on owner intake absent means "keep what is on file" —
        // so the staff value survives as a staff value rather than being
        // promoted to an owner's answer.
        setDraft({
          answers: view.answers,
          questions: view.questions,
          fillable: view.fillable,
          loaded: true,
        });
      } catch (err) {
        if (live) setError(submitError(err));
      } finally {
        if (live) setLoading(false);
      }
    })();
    return () => {
      live = false;
    };
    // setDraft is a fresh closure each render; re-running on it would loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, reloadToken]);

  if (loading && !draft.loaded) return <div className="oc-meta">Loading their questions…</div>;
  if (error) {
    return (
      <div className="alert" data-tone="danger" role="alert">
        <div className="alert-body">{error}</div>
      </div>
    );
  }
  if (draft.loaded && !draft.fillable) {
    return (
      <div className="oc-meta">
        This intake is closed — the agreement has moved on, so there is nothing
        left to record against it.
      </div>
    );
  }
  if (draft.questions.length === 0) {
    return <div className="oc-meta">There are no questions outstanding on this intake.</div>;
  }

  const set = (key: string, value: unknown) =>
    setDraft({ ...draft, answers: merge(draft.answers, { [key]: value }) });

  const bySection = OWNER_SECTION_ORDER.map((section) => ({
    section,
    questions: draft.questions.filter((q) => q.section === section),
  })).filter((g) => g.questions.length > 0);

  return (
    <>
      {bySection.map(({ section, questions }) => (
        <div key={section}>
          <div className="oc-kicker">{OWNER_SECTION_LABEL[section]}</div>
          {questions.map((q) => {
            const key = q.id as string;
            const raw = ownerValue(draft.answers, q);
            const invalid =
              attempted &&
              q.required &&
              ownerQuestionUnanswered(draft.answers as Record<string, unknown>, { id: key });
            // A confirm-with-owner question is asked of the OWNER as "we think
            // yes — just confirming". For a staff recorder that framing is
            // wrong: they are writing down an answer, not giving one.
            const prompt = q.confirm
              ? `${q.prompt} — WHP proposed ${q.confirm.staffValue ? 'yes' : 'no'}; what did they say?`
              : q.prompt;

            if (q.kind === 'boolean') {
              return (
                <YesNoField
                  key={key}
                  label={prompt}
                  value={typeof raw === 'boolean' ? raw : undefined}
                  disabled={disabled}
                  onChange={(v) => set(key, v)}
                />
              );
            }
            if (q.kind === 'select') {
              return (
                <ChoiceField
                  key={key}
                  label={prompt}
                  value={typeof raw === 'string' ? raw : undefined}
                  options={(q.options ?? []).map((o) => ({ value: o.value, label: o.label }))}
                  required={q.required}
                  invalid={invalid}
                  disabled={disabled}
                  onChange={(v) => set(key, v)}
                />
              );
            }
            if (q.kind === 'textarea') {
              return (
                <TextAreaField
                  key={key}
                  label={prompt}
                  value={typeof raw === 'string' ? raw : ''}
                  required={q.required}
                  invalid={invalid}
                  disabled={disabled}
                  onChange={(v) => set(key, v)}
                />
              );
            }
            return (
              <TextField
                key={key}
                label={prompt}
                value={typeof raw === 'string' ? raw : ''}
                required={q.required}
                invalid={invalid}
                disabled={disabled}
                onChange={(v) => set(key, v)}
              />
            );
          })}
        </div>
      ))}
      <div className="vd-record-note">
        Leaving an answer blank keeps whatever is already on file — it does not
        clear it. Answering a question may reveal another one, just as it does on
        the owner's own form.
      </div>
    </>
  );
}

// ---------------------------------------------------------------------------
// The form
// ---------------------------------------------------------------------------

type Draft =
  | { form: 'intake_designation'; designation: IntakeDesignationData }
  | { form: 'intake_yard_care'; yardCare: { arrangement?: 'resident_maintains' | 'lessor_arranges' } }
  | { form: 'intake_animals'; animals: StaffAnimalDraft[] }
  | { form: 'rental_verification'; verification: VerificationSubmissionBody }
  | {
      form: 'owner_intake';
      answers: OwnerIntakeAnswers;
      /** Server-computed; the editor loads them. */
      questions: OwnerIntakeQuestion[];
      fillable: boolean;
      loaded: boolean;
    };

function emptyDraft(form: string): Draft | null {
  switch (form) {
    case 'intake_designation':
      return { form, designation: {} };
    case 'intake_yard_care':
      return { form, yardCare: {} };
    case 'intake_animals':
      return { form, animals: [newAnimal()] };
    case 'rental_verification':
      return { form, verification: {} };
    case 'owner_intake':
      return { form, answers: {}, questions: [], fillable: true, loaded: false };
    default:
      return null;
  }
}

/** What is still missing, in the operator's words. Empty = ready to send. */
function missingFrom(draft: Draft): string[] {
  switch (draft.form) {
    case 'intake_designation':
      return draft.designation.designeeName?.trim() ? [] : ["the designee's name"];
    case 'intake_yard_care':
      return draft.yardCare.arrangement ? [] : ['who takes care of the yard'];
    case 'intake_animals':
      // The same required answers a household must give (WEL-173), said in
      // the operator's words: "Rex's breed and weight".
      return staffAnimalGaps(draft.animals);
    case 'rental_verification':
      return draft.verification.respondent?.name?.trim()
        ? []
        : ['the name of the person who told you'];
    case 'owner_intake':
      // Until the questions land there is nothing to judge — the Record button
      // is held by `loaded` rather than by a made-up complaint.
      return draft.loaded
        ? ownerMissing(
            draft.answers as Record<string, unknown>,
            draft.questions.map((q) => ({
              id: q.id as string,
              prompt: q.prompt,
              required: q.required,
            })),
          )
        : [];
  }
}

function answersFrom(draft: Draft): RecordAnswers {
  switch (draft.form) {
    case 'intake_designation':
      return { designation: draft.designation };
    case 'intake_yard_care':
      // missingFrom() has already guaranteed the arrangement is set.
      return { yardCare: { arrangement: draft.yardCare.arrangement as 'resident_maintains' | 'lessor_arranges' } };
    case 'intake_animals':
      return { animals: draft.animals.map(staffAnimalToInput) };
    case 'rental_verification':
      return { verification: draft.verification };
    case 'owner_intake':
      return { answers: draft.answers };
  }
}

function submitError(err: unknown): string {
  if (err instanceof ApiError) {
    const body = err.body as { error?: string; message?: string } | null;
    return body?.message ?? body?.error ?? `Couldn't record the answers (HTTP ${err.status}).`;
  }
  return err instanceof Error ? err.message : "Couldn't record the answers.";
}

const CHANNELS: Array<{ value: RecordChannel; label: string }> = [
  { value: 'email', label: 'They emailed it' },
  { value: 'phone', label: 'They told me on the phone' },
  { value: 'in_person', label: 'They told me in person' },
  { value: 'mail', label: 'They posted it' },
  { value: 'other', label: 'Some other way' },
];

export function RecordOnBehalfForm({
  kind,
  id,
  form,
  recipients,
  onRecorded,
}: {
  kind: string;
  id: string;
  /** Which staff form to render — from recordFormForKind(). */
  form: string;
  /** Who the answers can be attributed to. */
  recipients: DocRecipient[];
  onRecorded: () => void;
}): React.JSX.Element | null {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState<Draft | null>(() => emptyDraft(form));
  const [attempted, setAttempted] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [reloadToken, setReloadToken] = useState(0);
  const [channel, setChannel] = useState<RecordChannel | ''>('');
  const [note, setNote] = useState('');
  const [onBehalfOf, setOnBehalfOf] = useState<string>(
    recipients.length === 1 ? (recipients[0]?.id ?? '') : '',
  );

  if (!draft) return null;

  const isOwnerIntake = draft.form === 'owner_intake';
  // Owner intake is the one form whose questions arrive asynchronously, and the
  // one that is locked from the server rather than by its own state.
  const notReady = isOwnerIntake && !draft.loaded;
  const locked = isOwnerIntake && draft.loaded && !draft.fillable;
  const recipient = recipients.find((r) => r.id === onBehalfOf) ?? null;
  // The server derives the recipient when it is unambiguous, so an unset
  // selection is only a problem when there is a real choice to make.
  const needsRecipient = recipients.length > 1 && !recipient;
  const missing = missingFrom(draft);
  const blocked = missing.length > 0 || needsRecipient || notReady || locked;

  const reset = () => {
    setDraft(emptyDraft(form));
    setAttempted(false);
    setNote('');
    setChannel('');
  };

  const submit = async () => {
    setBusy(true);
    try {
      // Assembled through the allowlist, not spread freehand: nothing but the
      // permitted answer key and the four attribution fields can reach the
      // wire. In particular there is no `complete` — staff transcribe, they do
      // not send. See buildRecordBody.
      const body = buildRecordBody(answersFrom(draft) as Record<string, unknown>, {
        onBehalfOf,
        onBehalfOfDisplay: recipient?.name,
        channel,
        note: note.trim(),
      }) as unknown as RecordOnBehalfBody;
      const res = await recordDocOnBehalf(kind, id, body);
      setConfirming(false);
      // Owner intake stays OPEN: answering a gate can reveal a dependent
      // question, so closing the form here would hide the next question behind
      // a second click nobody would know to make.
      if (isOwnerIntake) {
        setAttempted(false);
        setReloadToken((n) => n + 1);
      } else {
        setOpen(false);
        reset();
      }
      // applied === false is NOT a failure: they answered first, which is the
      // outcome we wanted. Saying "recorded" there would be a lie.
      if (res.applied === false) {
        toast.info(
          res.message ??
            'They had already answered, so their own answers stand — nothing was overwritten.',
        );
      } else {
        toast.success(
          res.message ??
            (recipient
              ? `Recorded on behalf of ${recipient.name}. The timeline now shows who entered it.`
              : 'Recorded. The timeline now shows who entered it.'),
        );
      }
      onRecorded();
    } catch (err) {
      toast.error(submitError(err));
    } finally {
      setBusy(false);
    }
  };

  if (!open) {
    return (
      <div style={{ marginTop: 'var(--space-4)' }}>
        <button
          type="button"
          className="btn"
          data-variant="secondary"
          data-size="sm"
          onClick={() => setOpen(true)}
        >
          Record their answers
        </button>
        <div className="oc-meta" style={{ marginTop: 4 }}>
          For answers they gave another way — by email, or over the phone. It is
          filed as yours, on their behalf, and their own form stays open to them.
        </div>
      </div>
    );
  }

  return (
    <div className="vd-record">
      <div className="vd-record-note">
        Their form stays open. If they submit it themselves, their own answers
        win and nothing here overwrites them.
      </div>

      <FieldGrid>
        {recipients.length > 0 && (
          <div className="field">
            <label htmlFor="vd-obo">
              On behalf of{recipients.length > 1 ? '' : ' (optional)'}
            </label>
            <select
              id="vd-obo"
              className="input"
              value={onBehalfOf}
              disabled={busy}
              {...(recipients.length > 1 ? { required: true } : {})}
              {...(attempted && needsRecipient ? { 'aria-invalid': 'true' as const } : {})}
              onChange={(e) => setOnBehalfOf(e.target.value)}
            >
              <option value="">
                {recipients.length > 1 ? 'Choose a person…' : 'Work it out from the request'}
              </option>
              {recipients.map((r) => (
                <option key={r.id} value={r.id}>
                  {r.name}
                  {r.roleLabel ? ` — ${r.roleLabel}` : ''}
                </option>
              ))}
            </select>
          </div>
        )}
        <div className="field">
          <label htmlFor="vd-channel">How they told us (optional)</label>
          <select
            id="vd-channel"
            className="input"
            value={channel}
            disabled={busy}
            onChange={(e) => setChannel(e.target.value as RecordChannel | '')}
          >
            <option value="">Not stated</option>
            {CHANNELS.map((c) => (
              <option key={c.value} value={c.value}>
                {c.label}
              </option>
            ))}
          </select>
        </div>
      </FieldGrid>

      <div className="field">
        <label htmlFor="vd-note">Note for the record (optional)</label>
        <input
          id="vd-note"
          type="text"
          className="input"
          placeholder="Emailed her answers 19 Aug, transcribed verbatim"
          value={note}
          disabled={busy}
          onChange={(e) => setNote(e.target.value)}
        />
      </div>

      <div className="vd-record-answers">
        {draft.form === 'intake_designation' && (
          <DesignationEditor
            draft={draft.designation}
            setDraft={(designation) => setDraft({ ...draft, designation })}
            attempted={attempted}
            disabled={busy}
          />
        )}
        {draft.form === 'intake_yard_care' && (
          <YardCareEditor
            draft={draft.yardCare}
            setDraft={(yardCare) => setDraft({ ...draft, yardCare })}
            attempted={attempted}
            disabled={busy}
          />
        )}
        {draft.form === 'intake_animals' && (
          <AnimalsEditor
            draft={draft.animals}
            setDraft={(animals) => setDraft({ ...draft, animals })}
            attempted={attempted}
            disabled={busy}
          />
        )}
        {draft.form === 'rental_verification' && (
          <VerificationEditor
            draft={draft.verification}
            setDraft={(verification) => setDraft({ ...draft, verification })}
            attempted={attempted}
            disabled={busy}
          />
        )}
        {draft.form === 'owner_intake' && (
          <OwnerIntakeEditor
            id={id}
            draft={draft}
            setDraft={(next) => setDraft({ form: 'owner_intake', ...next })}
            attempted={attempted}
            disabled={busy}
            reloadToken={reloadToken}
          />
        )}
      </div>

      {attempted && (missing.length > 0 || needsRecipient) && (
        <div className="alert" data-tone="warning" role="alert">
          <div className="alert-body">
            {needsRecipient
              ? 'Choose whose answers these are — the record is filed against a person.'
              : `Still needed before this can be recorded: ${missing.join(', ')}.`}
          </div>
        </div>
      )}

      <div style={{ display: 'flex', gap: 'var(--space-2)', flexWrap: 'wrap' }}>
        {!locked && (
          <button
            type="button"
            className="btn"
            data-variant="primary"
            data-size="sm"
            disabled={busy || notReady}
            onClick={() => {
              setAttempted(true);
              if (!blocked) setConfirming(true);
            }}
          >
            Record
          </button>
        )}
        <button
          type="button"
          className="btn"
          data-variant="ghost"
          data-size="sm"
          disabled={busy}
          onClick={() => {
            setOpen(false);
            reset();
          }}
        >
          Cancel
        </button>
      </div>

      <ConsequenceConfirm
        open={confirming}
        title={`Record ${recipient ? `${recipient.name}'s` : 'these'} answers?`}
        consequence={
          recipient
            ? `These answers are filed against ${recipient.name} and attributed to you. The record will read "recorded by you on behalf of ${recipient.name}", so anyone reading it later knows the answers arrived another way. ${recipient.name}'s own form is untouched and still open to them — if they submit it, their answers win.`
            : 'These answers are filed against this request and attributed to you. The recipient’s own form is untouched and still open to them — if they submit it, their answers win.'
        }
        confirmLabel="Record answers"
        tone="default"
        busy={busy}
        onConfirm={() => void submit()}
        onCancel={() => {
          if (!busy) setConfirming(false);
        }}
      />
    </div>
  );
}
